import { Injectable } from '@angular/core';
import { Actions, Select, Store, ofActionDispatched } from '@ngxs/store';
import { ExtendedAttributeFilterInterface } from 'app/library/all-users/interfaces/extended-attribute-filer.interface';
import { IModuleUserDateRangeFilters } from 'app/library/module-user/interfaces/module-user-date-range-filters.interface';
import { ModuleUser } from 'app/library/module-user/models/module-user.model';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { Observable, race } from 'rxjs';
import {
    FetchCourseOfferingParticipants,
    FetchCourseOfferingParticipantsFailure,
    FetchCourseOfferingParticipantsSuccess,
    FetchProgramExecutionCourseOfferingParticipants,
    FetchProgramExecutionCourseOfferingParticipantsFailure,
    FetchProgramExecutionCourseOfferingParticipantsSuccess,
    FetchProgramManagerBookings,
    FetchProgramManagerBookingsFailure,
    FetchProgramManagerBookingsSuccess,
    FetchProgramParticipantBookings,
    FetchProgramParticipantBookingsFailure,
    FetchProgramParticipantBookingsSuccess,
    UnbookManager,
    UnbookManagerFailure,
    UnbookManagerSuccess,
    UnbookParticipant,
    UnbookParticipantFailure,
    UnbookParticipantSuccess,
} from '../actions';
import { CourseOfferingParticipantsState } from '../models/course-offering-participants.state';
import { IUnbookProgramExecutionUsersActionPayload } from '../interfaces/program-execution-unbook-users-action.action-payload.interface';
import { IParticipantBookingActionPayload } from '../interfaces/participant-booking-action.action-payload.interface';
import { ParticipantBooking } from '../models/participant-booking.model';
import { filter, map, take } from 'rxjs/operators';

@Injectable()
export class CourseOfferingParticipantsService {
    @Select(CourseOfferingParticipantsState.getEntities)
    moduleParticipant$: Observable<ModuleUser[]>;

    constructor(private _store: Store, private _action$: Actions, private _actionHelper: NgxsActionHelper) {}

    getModuleParticipants(): ModuleUser[] {
        return this._store.selectSnapshot(CourseOfferingParticipantsState.getEntities);
    }

    async fetchParticipantsByCourseOfferingId(
        courseOfferingId: string,
        search?: string,
        sortBy?: string,
        sortOrder?: string,
        pageSize?: number,
        pageNumber?: number,
        withoutCertificate?: boolean,
        selectedCertificateIds?: string[],
        selectedStatusIds?: string[],
        selectedExecutionIds?: string[],
        dateRangeFilters?: IModuleUserDateRangeFilters,
        audienceIds?: string[],
        teamIds?: string[],
        extendedAttributesFilters?: ExtendedAttributeFilterInterface[],
        isUnassignedSelectedAudiences?: boolean,
        isUnassignedSelectedTeams?: boolean,
        excludedTeamIds?: string[],
        teamRoleIds?: string[],
        columns?: string[]
    ): Promise<boolean> {
        this._store.dispatch(
            new FetchCourseOfferingParticipants({
                courseOfferingId: courseOfferingId,
                search,
                sortBy,
                sortOrder,
                pageSize,
                pageNumber,
                withoutCertificate,
                selectedCertificateIds,
                selectedStatusIds,
                selectedExecutionIds,
                dateRangeFilters,
                audienceIds,
                teamIds,
                extendedAttributesFilters,
                isUnassignedSelectedAudiences,
                isUnassignedSelectedTeams,
                excludedTeamIds,
                teamRoleIds,
                columns,
            })
        );

        return await this._actionHelper.race(FetchCourseOfferingParticipantsSuccess, FetchCourseOfferingParticipantsFailure).toPromise();
    }

    async fetchProgramExecutionParticipantsByCourseOfferingId(
        courseOfferingId: string,
        programExecutionId: string,
        search?: string,
        sortBy?: string,
        sortOrder?: string,
        pageSize?: number,
        pageNumber?: number,
        withoutCertificate?: boolean,
        selectedCertificateIds?: string[],
        selectedStatusIds?: string[],
        selectedExecutionIds?: string[],
        dateRangeFilters?: IModuleUserDateRangeFilters,
        audienceIds?: string[],
        teamIds?: string[],
        extendedAttributesFilters?: ExtendedAttributeFilterInterface[],
        isUnassignedSelectedAudiences?: boolean,
        isUnassignedSelectedTeams?: boolean,
        excludedTeamIds?: string[],
        teamRoleIds?: string[],
        columns?: string[]
    ): Promise<boolean> {
        this._store.dispatch(
            new FetchProgramExecutionCourseOfferingParticipants({
                courseOfferingId: courseOfferingId,
                programExecutionId: programExecutionId,
                search,
                sortBy,
                sortOrder,
                pageSize,
                pageNumber,
                withoutCertificate,
                selectedCertificateIds,
                selectedStatusIds,
                selectedExecutionIds,
                dateRangeFilters,
                audienceIds,
                teamIds,
                extendedAttributesFilters,
                isUnassignedSelectedAudiences,
                isUnassignedSelectedTeams,
                excludedTeamIds,
                teamRoleIds,
                columns,
            })
        );

        return await this._actionHelper.race(FetchProgramExecutionCourseOfferingParticipantsSuccess, FetchProgramExecutionCourseOfferingParticipantsFailure).toPromise();
    }

    async unbookParticipant(payload: IUnbookProgramExecutionUsersActionPayload): Promise<boolean> {
        this._store.dispatch(new UnbookParticipant(payload));

        return await this._actionHelper.race(UnbookParticipantSuccess, UnbookParticipantFailure).toPromise();
    }

    async unbookManager(payload: IUnbookProgramExecutionUsersActionPayload): Promise<boolean> {
        this._store.dispatch(new UnbookManager(payload));

        return await this._actionHelper.race(UnbookManagerSuccess, UnbookManagerFailure).toPromise();
    }

    async fetchParticipantBookings(payload: IParticipantBookingActionPayload): Promise<ParticipantBooking[]> {
        this._store.dispatch(new FetchProgramParticipantBookings(payload));

        return race(
            this._action$.pipe(
                filter((participantExecutionChanges) => !!participantExecutionChanges),
                ofActionDispatched(FetchProgramParticipantBookingsSuccess),
                map((action: FetchProgramParticipantBookingsSuccess) => {
                    return action.payload;
                })
            ),
            this._action$.pipe(
                ofActionDispatched(FetchProgramParticipantBookingsFailure),
                map((action: FetchProgramParticipantBookingsFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchManagerBookings(payload: IParticipantBookingActionPayload): Promise<ParticipantBooking[]> {
        this._store.dispatch(new FetchProgramManagerBookings(payload));

        return race(
            this._action$.pipe(
                filter((participantExecutionChanges) => !!participantExecutionChanges),
                ofActionDispatched(FetchProgramManagerBookingsSuccess),
                map((action: FetchProgramManagerBookingsSuccess) => {
                    return action.payload;
                })
            ),
            this._action$.pipe(
                ofActionDispatched(FetchProgramManagerBookingsFailure),
                map((action: FetchProgramManagerBookingsFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }
}
